import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { MENS_APPARATUS, WOMENS_APPARATUS } from '../../constants/competitionConstants';
import { decodeHtmlEntities } from '../../utils/textUtils';
import { Box, Card, Typography, List, ListItem, ListItemText, Divider, Stack, Button, Badge, IconButton, Collapse, Chip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SquadCardSkeleton from './SquadCardSkeleton';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
  useDroppable,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { updateCompetition, setToast } from '../../store/competitionSlice';
import { useSelector, useDispatch } from 'react-redux';

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
};

const SortableSquadItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    height: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  };

  return (
    <Box ref={setNodeRef} style={style} {...attributes} sx={{ height: 'auto', width: '100%' }}>
      {children(listeners, isDragging)}
    </Box>
  );
};

const SortableAthleteItem = React.memo(({ id, children, disabled, onClick, selected, selectionIndex, isFirstInGroup, forceDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: dndIsDragging,
  } = useSortable({ id, disabled });

  const isDragging = dndIsDragging || forceDragging;

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    // Hide the item completely when dragging to only show blue line indicator
    opacity: isDragging ? 0 : 1,
    position: 'relative',
    // Prevent the item from taking up space when hidden
    height: isDragging ? 0 : 'auto',
    overflow: isDragging ? 'hidden' : 'visible',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} onClick={onClick}>
      {children}
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: isFirstInGroup ? 0 : -8, // Compensate for 8px top border on first item
            right: -8,
            zIndex: 2,
            bgcolor: '#ff9800', // Orange to pop
            color: 'white',
            borderRadius: '50%',
            width: 24, // Slightly larger
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.85rem',
            fontWeight: 'bold',
            boxShadow: 2,
            border: '2px solid white' // Add white border for contrast
          }}
        >
          {selectionIndex}
        </Box>
      )}
    </div>
  );
});

const AthleteListItem = React.memo(({
  gymnast,
  index,
  gridIndex,
  squadNum,
  selectedRotation,
  isSelected,
  selectionIndex,
  isFirstInGroup,
  isLastInGroup,
  moveAthletesMode,
  competitionColors,
  teamColors,
  levelColors,
  highlightTeamsEnabled,
  highlightLevelsEnabled,
  highlightedTeamId,
  teamMap,
  session,
  scratchMode,
  isScratchSelected,
  isScratched,
  onClick
}) => {
  const showScratch = isScratchSelected || isScratched;

  return (
    <ListItem
      sx={{
        py: 0,
        px: 0,
        mx: -2,
        width: 'calc(100% + 32px)',
        paddingLeft: 2,
        paddingRight: 2,
        minHeight: 'unset',
        userSelect: moveAthletesMode ? 'none' : 'auto',
        backgroundColor: (highlightLevelsEnabled && levelColors[gymnast.level])
          ? `${levelColors[gymnast.level]}99`
          : (highlightTeamsEnabled && teamColors[gymnast.teamId]) ||
            (gymnast.teamId === highlightedTeamId)
            ? `${teamColors[gymnast.teamId] || competitionColors?.primary}99`
            : 'transparent',
        borderLeft: isSelected ? '8px solid #ff9800' : 'none',
        borderRight: isSelected ? '8px solid #ff9800' : 'none',
        borderTop: isSelected && isFirstInGroup ? '8px solid #ff9800' : 'none',
        borderBottom: isSelected && isLastInGroup ? '8px solid #ff9800' : 'none',
        marginTop: '0',
        marginBottom: '0',
        clipPath: isSelected && !isFirstInGroup ? 'inset(0px -20px -20px -20px)' : 'none',
        borderTopLeftRadius: isSelected && isFirstInGroup ? '4px' : '0',
        borderTopRightRadius: isSelected && isFirstInGroup ? '4px' : '0',
        borderBottomLeftRadius: isSelected && isLastInGroup ? '4px' : '0',
        borderBottomRightRadius: isSelected && isLastInGroup ? '4px' : '0',
        transform: 'none', // Remove transform to prevent layout shift
        boxShadow: isSelected
          ? `0 ${isFirstInGroup ? '4px' : '0'} ${isLastInGroup ? '8px' : '4px'} rgba(0,0,0,0.15)`
          : 'none',
        transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
        zIndex: isSelected ? 2 : 1,
        position: 'relative',
        '&:hover': !moveAthletesMode ? {
          backgroundColor: (highlightLevelsEnabled && levelColors[gymnast.level])
            ? `${levelColors[gymnast.level]}CC` // Slightly lighter when level highlighted
            : (highlightTeamsEnabled && teamColors[gymnast.teamId]) ||
              (gymnast.teamId === highlightedTeamId)
              ? `${teamColors[gymnast.teamId] || competitionColors?.primary}CC` // Slightly lighter when team highlighted
              : theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)' // Subtle white overlay in dark mode
                : 'rgba(0, 0, 0, 0.04)', // Subtle dark overlay in light mode
          boxShadow: '0 0 0 1px rgba(33, 150, 243, 0.3) inset' // Subtle border hint
        } : {},
        cursor: onClick ? 'pointer' : moveAthletesMode ? 'pointer' : 'default',
        opacity: showScratch ? 0.65 : 1
      }}
      onClick={onClick}
    >
      <Stack 
        direction="row" 
        spacing={0.5} 
        alignItems="center" 
        sx={{ 
          width: '100%',
          minHeight: 'unset',
          py: 0.25
        }}
      >
        {moveAthletesMode && (
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', opacity: 0.5 }}>
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: `${competitionColors?.primary || '#2196f3'}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme => theme.palette.mode === 'dark' 
              ? '#fff'
              : competitionColors?.primary || '#2196f3',
            fontWeight: 'bold',
            fontSize: '0.75rem',
            flexShrink: 0,
            boxShadow: theme => theme.palette.mode === 'dark'
              ? 'inset 0 0 0 1px rgba(255,255,255,0.1)'
              : 'none'
          }}
        >
          {index + 1}
        </Box>
        <ListItemText
          primary={
            <Typography component="div" sx={{ lineHeight: 1.2, fontSize: '0.875rem', textDecoration: showScratch ? 'line-through' : 'none' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>{decodeHtmlEntities(`${gymnast.firstName} ${gymnast.lastName}`)}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {session.gender?.toLowerCase() === 'm' && (() => {
                    // Use stored original position if available, otherwise use current index
                    const originalIndex = gymnast._originalPositionInSquad !== undefined
                      ? gymnast._originalPositionInSquad
                      : index;

                    // Only show apparatus if athlete was originally in top 6
                    if (originalIndex >= 6) return null;

                    // Calculate apparatus based on squad + original position
                    const apparatusIndex = ((squadNum - 1) + originalIndex) % 6;
                    const normalizedIndex = apparatusIndex < 0 ? apparatusIndex + 6 : apparatusIndex;
                    return (
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 600,
                          color: '#ffffff',
                          backgroundColor: theme => theme.palette.mode === 'dark' ? '#555555' : '#757575',
                          minWidth: '20px',
                          width: '20px',
                          height: '20px',
                          px: 0.2,
                          py: 0.05,
                          borderRadius: 0.5,
                          fontSize: '0.6rem',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          flexShrink: 0,
                          fontFamily: 'monospace'
                        }}
                      >
                        {MENS_APPARATUS[normalizedIndex]?.abbrev}
                      </Typography>
                    );
                  })()}
                  <Typography component="span" sx={{ fontWeight: 'bold', textAlign: 'right', minWidth: '40px', fontSize: '0.875rem' }}>
                    #{gymnast.number || '?'}
                  </Typography>
                </Box>
              </Box>
            </Typography>
          }
          secondary={
             <Box component="span" sx={{ fontSize: '0.75rem', lineHeight: 1.1, display: 'block', textDecoration: showScratch ? 'line-through' : 'none' }}>
               Level {gymnast.level} • {decodeHtmlEntities(teamMap[gymnast.teamId]?.shortName || teamMap[gymnast.teamId]?.name) || 'No Team'}
             </Box>
          }
        />
      </Stack>
      {showScratch && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: 12,
            transform: 'translateY(-50%)',
            color: theme => theme.palette.error.main,
            fontWeight: 700,
            fontSize: '1.1rem',
            pointerEvents: 'none'
          }}
        >
          ×
        </Box>
      )}
    </ListItem>
  );
});

const MultiDragPreview = ({
  activeId,
  selectedIds,
  squadGymnastsList,
  squads,
  competitionColors,
  teamColors,
  levelColors,
  highlightTeamsEnabled,
  highlightLevelsEnabled,
  highlightedTeamId,
  session,
  teamMap
}) => {
  // Identify the active squad and index
  let activeSquadNumber = null;
  let activeIndex = -1;

  for (const [squadNum, list] of Object.entries(squadGymnastsList)) {
    const idx = list.findIndex(g => g.id === activeId);
    if (idx !== -1) {
      activeSquadNumber = parseInt(squadNum);
      activeIndex = idx;
      break;
    }
  }

  // Build preview structure
  const previewItems = [];
  let offsetIndex = 0; // To calculate Y offset of active item

  // Iterate over squads to maintain order
  // If dragging across squads, we usually just show the active squad's structure or stack them
  // For "gap" preservation, we focus on the active squad primarily
  
  const relevantSquads = squads.filter(squadNum => {
    const list = squadGymnastsList[squadNum] || [];
    return list.some(g => selectedIds.includes(g.id));
  });

  relevantSquads.forEach((squadNum) => {
    const list = squadGymnastsList[squadNum];
    const squadSelectedIds = selectedIds.filter(id => list.some(g => g.id === id));
    
    if (squadSelectedIds.length === 0) return;

    // Get indices of selected items in this squad
    const selectedIndices = list.map((g, i) => selectedIds.includes(g.id) ? i : -1).filter(i => i !== -1);
    const minIndex = Math.min(...selectedIndices);
    const maxIndex = Math.max(...selectedIndices);

    // If this is the active squad, we preserve the gaps between min and max
    // If not, maybe we compress? Let's preserve gaps for consistency if user wants "structure"
    
    for (let i = minIndex; i <= maxIndex; i++) {
       const gymnast = list[i];
       const isSelected = selectedIds.includes(gymnast.id);
       
       if (isSelected) {
         if (gymnast.id === activeId) {
            offsetIndex = previewItems.length;
         }
         previewItems.push({ type: 'item', gymnast, index: i, squadNum });
       } else {
         previewItems.push({ type: 'gap' });
       }
    }
    
    // Add a divider gap between squads if multiple
    if (relevantSquads.length > 1 && squadNum !== relevantSquads[relevantSquads.length - 1]) {
       // previewItems.push({ type: 'squad-gap' }); // Optional
    }
  });

  // Calculate offset (assuming approx 60px height per item)
  const ITEM_HEIGHT = 52; // Approximate height of dense ListItem + padding
  const offsetY = offsetIndex * ITEM_HEIGHT;

  return (
    <Box sx={{ 
      position: 'relative', 
      width: 300, // Match card width approx
      transform: `translateY(-${offsetY}px)`,
      pointerEvents: 'none' 
    }}>
      <Card sx={{ 
        bgcolor: 'background.paper', 
        boxShadow: 6, 
        p: 1, // Add padding to match source context
        // overflow: 'hidden', // Removed to allow negative margins to show borders
      }}>
        <List dense sx={{ p: 0 }}>
          {previewItems.map((item, i) => {
             if (item.type === 'gap') {
               return <Box key={`gap-${i}`} sx={{ height: ITEM_HEIGHT, opacity: 0.2, borderLeft: '2px dashed #ccc', ml: 4, my: 0 }} />;
             }
             
             const { gymnast, index, squadNum } = item;
             // Determine grouping visual for preview
             // Since we are reconstructing, we can check neighbors in 'previewItems'
             const prevItem = previewItems[i-1];
             const nextItem = previewItems[i+1];
             
             const isFirst = !prevItem || prevItem.type === 'gap' || prevItem.squadNum !== squadNum;
             const isLast = !nextItem || nextItem.type === 'gap' || nextItem.squadNum !== squadNum;
             
             // Grid index for apparatus
             // We need to find the grid index for this squad
             const gridIndex = squads.indexOf(squadNum);

             return (
               <AthleteListItem
                  key={gymnast.id}
                  gymnast={gymnast}
                  index={index}
                  gridIndex={gridIndex}
                  isSelected={true} // Always selected in preview
                  selectionIndex={selectedIds.indexOf(gymnast.id) + 1}
                  isFirstInGroup={isFirst}
                  isLastInGroup={isLast}
                  moveAthletesMode={true}
                  competitionColors={competitionColors}
                  teamColors={teamColors}
                  levelColors={levelColors}
                  highlightTeamsEnabled={highlightTeamsEnabled}
                  highlightLevelsEnabled={highlightLevelsEnabled}
                  highlightedTeamId={highlightedTeamId}
                  teamMap={teamMap}
                  session={session}
                  scratchMode={false}
                  isScratchSelected={false}
                  isScratched={gymnast.scratched}
               />
             );
          })}
        </List>
      </Card>
    </Box>
  );
};

const DroppableSquad = ({ id, children }) => {
  const { setNodeRef } = useDroppable({ id });
  return (
    <Box ref={setNodeRef} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {children}
    </Box>
  );
};

const SessionSquad = ({
  session,
  competitionColors,
  flightNumber,
  onUpdateOrders,
  highlightedTeamId,
  onTeamHighlight,
  selectedRotation,
  selectedRotationB,
  highlightTeamsEnabled,
  highlightLevelsEnabled,
  teamColors,
  levelColors,
  moveSquadsMode,
  moveAthletesMode,
  scratchMode,
  scratchSelections,
  selectedAthleteIds,
  setSelectedAthleteIds,
  onToggleScratch,
  selectedFilters,
  allFlights = false,
  viewByTeams = false
}) => {
  const dispatch = useDispatch();
  const [squadGymnastsList, setSquadGymnastsList] = useState({});
  const [localSquads, setLocalSquads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeAthleteId, setActiveAthleteId] = useState(null);
  // const [selectedAthleteIds, setSelectedAthleteIds] = useState([]); // Removed local state
  const [lastInteractedAthleteId, setLastInteractedAthleteId] = useState(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [collapsedFlights, setCollapsedFlights] = useState({});
  const [dragOverFlight, setDragOverFlight] = useState(null);
  const [dragSourceFlight, setDragSourceFlight] = useState(null);

  // Progressive loading state
  const [isLoadingSquads, setIsLoadingSquads] = useState(true);
  const [loadedSquads, setLoadedSquads] = useState(new Set());
  const squadRefs = useRef({});

  const currentCompetition = useSelector(state => state.competition.currentCompetition);
  const scratchSelectionSet = useMemo(() => new Set(scratchSelections || []), [scratchSelections]);

  // Find the rotation that matches this session's rotationId
  const rotation = currentCompetition?.rotations?.find(
    r => r.id === session.rotationId
  );

  // Get all gymnasts and teams for this competition
  const gymnasts = currentCompetition?.gymnasts || [];
  const teams = currentCompetition?.teams || [];

  // Create a map of team IDs to team objects for faster lookup
  const teamMap = useMemo(() => {
    return teams.reduce((acc, team) => {
      acc[Number(team.id)] = team;
      return acc;
    }, {});
  }, [teams]);

  // Memoize filtered gymnasts for this session to avoid re-filtering on every render
  const sessionGymnasts = useMemo(() =>
    gymnasts.filter(g => Number(g.session) === Number(session.sessionNumber)),
    [gymnasts, session.sessionNumber]
  );

  // Memoize filter criteria for better performance
  const filterCriteria = useMemo(() => {
    if (!selectedFilters || selectedFilters.length === 0) return null;

    return {
      levelFilters: selectedFilters.filter(f => f.type === 'level'),
      teamFilters: selectedFilters.filter(f => f.type === 'team'),
      flightFilters: selectedFilters.filter(f => f.type === 'flight')
    };
  }, [selectedFilters]);

  // Create an array of squad numbers in rotation order
  const squads = useMemo(() => {
    if (!rotation?.numSquads) return [];

    const baseOrder = rotation?.squadAssignments?.[0]
      ? rotation.squadAssignments[0].split('').map(num => parseInt(num))
      : Array.from({ length: rotation.numSquads }, (_, i) => i + 1);

    if (selectedRotation > 1) {
      const rotationOffset = selectedRotation - 1;
      // Move squads to the right (take from end, move to front)
      return [
        ...baseOrder.slice(-rotationOffset),
        ...baseOrder.slice(0, -rotationOffset)
      ];
    }
    return baseOrder;
  }, [rotation?.numSquads, rotation?.squadAssignments, selectedRotation]);

  // Memoized Set for O(1) squad key lookups (used in collision detection)
  const localSquadsSet = useMemo(() => new Set(localSquads), [localSquads]);

  // Memoized Map for O(1) athlete-to-squad lookups (used in handleDragOver and collision detection)
  const athleteToSquadMap = useMemo(() => {
    const map = new Map();
    Object.entries(squadGymnastsList).forEach(([squadKey, athletes]) => {
      athletes.forEach(athlete => {
        map.set(athlete.id, squadKey);
      });
    });
    return map;
  }, [squadGymnastsList]);

  useEffect(() => {
    if (allFlights) {
      // Create combined list of flight-squad keys
      const flightsToInclude = Array.from({ length: session.numFlights || 1 }, (_, i) => i + 1);
      const combinedSquads = [];
      flightsToInclude.forEach(flightNum => {
        squads.forEach(squadNumber => {
          combinedSquads.push(`${flightNum}-${squadNumber}`);
        });
      });
      setLocalSquads(combinedSquads);
    } else {
      // Convert to strings for consistency with DnD Kit which uses string IDs
      setLocalSquads(squads.map(s => String(s)));
    }
  }, [squads, allFlights, session.numFlights]);

  useEffect(() => {
    if (!moveAthletesMode) {
      setSelectedAthleteIds([]);
      setLastInteractedAthleteId(null);
    }
  }, [moveAthletesMode]);

  // Process squad data asynchronously in batches to avoid blocking
  useEffect(() => {
    if (!rotation?.numSquads) {
      setSquadGymnastsList({});
      setIsLoadingSquads(false);
      return;
    }

    // Start loading immediately
    setIsLoadingSquads(true);

    // Determine which flights to include
    const flightsToInclude = allFlights
      ? Array.from({ length: session.numFlights || 1 }, (_, i) => i + 1)
      : [flightNumber];

    // Build list of all squad keys that need processing
    const squadKeysToProcess = [];
    flightsToInclude.forEach(currentFlightNum => {
      squads.forEach(squadNumber => {
        const squadKey = allFlights ? `${currentFlightNum}-${squadNumber}` : String(squadNumber);
        squadKeysToProcess.push({ squadKey, currentFlightNum, squadNumber });
      });
    });

    // Process squads in batches asynchronously
    const processSquadBatch = (batchIndex) => {
      const batchSize = 3;
      const startIdx = batchIndex * batchSize;
      const endIdx = Math.min(startIdx + batchSize, squadKeysToProcess.length);

      if (startIdx >= squadKeysToProcess.length) {
        setIsLoadingSquads(false);
        return;
      }

      // Process this batch
      const batchUpdates = {};
      for (let i = startIdx; i < endIdx; i++) {
        const { squadKey, currentFlightNum, squadNumber } = squadKeysToProcess[i];

        let squadGymnasts = sessionGymnasts.filter(gymnast => {
          // Basic filters
          const matchesSquad = gymnast.squad === String.fromCharCode(64 + squadNumber);
          const matchesFlight = gymnast.flight === String.fromCharCode(64 + currentFlightNum);
          const shouldShow = scratchMode || !gymnast.scratched;

          // Apply selected filters if any (using memoized criteria)
          let matchesFilters = true;
          if (filterCriteria) {
            const { levelFilters, teamFilters, flightFilters } = filterCriteria;

            let matchesLevel = true;
            let matchesTeam = true;
            let matchesFlightFilter = true;

            if (levelFilters.length > 0) {
              matchesLevel = levelFilters.some(f => gymnast.level === f.value);
            }

            if (teamFilters.length > 0) {
              matchesTeam = teamFilters.some(f => String(gymnast.teamId) === String(f.value));
            }

            if (flightFilters.length > 0) {
              matchesFlightFilter = flightFilters.some(f => gymnast.flight === f.value);
            }

            matchesFilters = matchesLevel && matchesTeam && matchesFlightFilter;
          }

          return matchesSquad && matchesFlight && shouldShow && matchesFilters;
        });

        // Sort by order
        squadGymnasts = squadGymnasts.sort((a, b) => {
          if (a.order || b.order) {
            return (Number(a.order) || Infinity) - (Number(b.order) || Infinity);
          }
          return (Number(a.number) || 0) - (Number(b.number) || 0);
        });

        // Store original position before rotation for apparatus assignment
        squadGymnasts = squadGymnasts.map((g, idx) => ({
          ...g,
          _originalPositionInSquad: idx
        }));

        // Apply rotation visual offset (drop top athletes to the bottom)
        // This should work regardless of whether explicit orders exist
        const rotationToUse = currentFlightNum === 1 ? selectedRotation : selectedRotationB;
        if (rotationToUse > 1) {
          const rotationOffset = rotationToUse - 1;
          squadGymnasts = [
            ...squadGymnasts.slice(rotationOffset),
            ...squadGymnasts.slice(0, rotationOffset)
          ];
        }

        batchUpdates[squadKey] = squadGymnasts;
      }

      // Update state with this batch
      setSquadGymnastsList(prev => ({ ...prev, ...batchUpdates }));

      // Mark these squads as loaded
      setLoadedSquads(prev => {
        const newSet = new Set(prev);
        Object.keys(batchUpdates).forEach(key => newSet.add(key));
        return newSet;
      });

      // Schedule next batch
      requestAnimationFrame(() => {
        setTimeout(() => processSquadBatch(batchIndex + 1), 30);
      });
    };

    // Start processing batches
    setTimeout(() => processSquadBatch(0), 0);
  }, [gymnasts, session.sessionNumber, flightNumber, selectedRotation, selectedRotationB, squads, filterCriteria, scratchMode, allFlights, session.numFlights, sessionGymnasts, rotation?.numSquads]);

  // Intersection Observer for lazy loading (for collapsed flights)
  useEffect(() => {
    if (!allFlights) return;

    const observerOptions = {
      root: null,
      rootMargin: '100px', // Start loading 100px before visible
      threshold: 0.01
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const squadKey = entry.target.dataset.squadKey;
          if (squadKey && !loadedSquads.has(squadKey)) {
            setLoadedSquads(prev => new Set([...prev, squadKey]));
          }
        }
      });
    }, observerOptions);

    // Observe all squad card containers
    Object.keys(squadRefs.current).forEach(squadKey => {
      const el = squadRefs.current[squadKey];
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [allFlights, localSquads]);

  const toggleFlightCollapse = (flightNum) => {
    setCollapsedFlights(prev => ({
      ...prev,
      [flightNum]: !prev[flightNum]
    }));
  };

  const handleAthleteClick = (e, id) => {
    if (scratchMode) {
      e.stopPropagation();
      onToggleScratch?.(id);
      setLastInteractedAthleteId(id);
      return;
    }

    if (!moveAthletesMode) return;
    e.stopPropagation();

    if (e.shiftKey && lastInteractedAthleteId) {
        let startSquad = null;
        let endSquad = null;
        let startList = null;
        let endList = null;

        // Find squad lists
        Object.entries(squadGymnastsList).forEach(([squadNum, list]) => {
            if (list.some(g => g.id === lastInteractedAthleteId)) {
                startSquad = squadNum;
                startList = list;
            }
            if (list.some(g => g.id === id)) {
                endSquad = squadNum;
                endList = list;
            }
        });

        // Range select if in same squad
        if (startSquad && endSquad && startSquad === endSquad) {
            const startIndex = startList.findIndex(g => g.id === lastInteractedAthleteId);
            const endIndex = endList.findIndex(g => g.id === id);
            
            const low = Math.min(startIndex, endIndex);
            const high = Math.max(startIndex, endIndex);
            
            const rangeIds = startList.slice(low, high + 1).map(g => g.id);
            
            setSelectedAthleteIds(prev => {
                const newSet = new Set(prev);
                rangeIds.forEach(rid => newSet.add(rid));
                return Array.from(newSet);
            });
            return;
        }
    }

    setLastInteractedAthleteId(id);
    setSelectedAthleteIds(prev => {
        if (prev.includes(id)) {
          return prev.filter(pid => pid !== id);
        } else {
          return [...prev, id];
        }
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = (event) => {
    setDropIndicator(null);
    if (moveSquadsMode) {
      setActiveId(event.active.id);
      // Track source flight for cross-flight detection
      if (allFlights) {
        const activeKey = String(event.active.id);
        setDragSourceFlight(activeKey.split('-')[0]);
      }
    } else if (moveAthletesMode) {
      setActiveAthleteId(event.active.id);
      // If dragging an item that is NOT selected, we should probably select it temporarily or just treat it as the only item
      // But if it IS selected, we are dragging the group
      if (!selectedAthleteIds.includes(event.active.id)) {
        // If we start dragging an unselected item, select only that item?
        // Or just treat it as a single drag without affecting selection state?
        // Let's treat it as single drag for now.
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveAthleteId(null);
    setDropIndicator(null);
    setDragSourceFlight(null);
    setDragOverFlight(null);
  };

  const handleSquadDragOver = ({ active, over }) => {
    if (!over || !allFlights) {
      setDragOverFlight(null);
      return;
    }
    const overKey = String(over.id);
    setDragOverFlight(overKey.split('-')[0]);
  };

  const handleDragOver = ({ active, over }) => {
    if (!over || !moveAthletesMode) {
      setDropIndicator(null);
      return;
    }

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) {
      setDropIndicator(null);
      return;
    }

    // O(1) lookups using memoized Set and Map
    const findSquad = (id) => {
      if (localSquadsSet.has(id)) return id; // O(1) Set lookup
      return athleteToSquadMap.get(id) || null; // O(1) Map lookup
    };

    const activeSquad = findSquad(activeId);
    const overSquad = findSquad(overId);

    const isMultiDrag = selectedAthleteIds.includes(activeId) && selectedAthleteIds.length > 1;

    // Show drop indicator for within-squad dragging too
    if (!activeSquad || !overSquad) {
      setDropIndicator(null);
      return;
    }

    // Calculate insertion index without modifying the list
    const destList = squadGymnastsList[overSquad] || [];
    let insertIndex = destList.length; // Default to end

    // If hovering over a specific athlete item, insert relative to it
    if (overId !== overSquad) {
      const overIndex = destList.findIndex(g => g.id === overId);
      if (overIndex !== -1) {
        // Use cursor position relative to item midpoint for stable insertion
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height / 2;

        insertIndex = overIndex + (isBelowOverItem ? 1 : 0);
      }
    }
    // When hovering over the squad container (not a specific item), keep default (end of list)

    // Set drop indicator for visual feedback
    setDropIndicator({ squad: overSquad, index: insertIndex });
  };

  const handleSquadDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setDragSourceFlight(null);
    setDragOverFlight(null);

    if (active && over && active.id !== over.id) {
      if (allFlights) {
        // Extract flight and squad numbers from the keys
        const activeKey = String(active.id);
        const overKey = String(over.id);
        const activeFlight = activeKey.split('-')[0];
        const overFlight = overKey.split('-')[0];

        // Block cross-flight squad moves (would require moving athletes between flights)
        if (activeFlight !== overFlight) {
          dispatch(setToast({ message: 'Cross-flight squad moves not supported', severity: 'info' }));
          return;
        }

        const activeSquadNum = activeKey.split('-')[1];
        const overSquadNum = overKey.split('-')[1];

        // Get current squad order from first flight
        const firstFlightSquads = localSquads.filter(key => String(key).startsWith('1-'));
        const currentOrder = firstFlightSquads.map(key => key.split('-')[1]);

        // Find positions and reorder
        const oldIdx = currentOrder.indexOf(activeSquadNum);
        const newIdx = currentOrder.indexOf(overSquadNum);

        if (oldIdx !== -1 && newIdx !== -1) {
          const newSquadOrder = arrayMove(currentOrder, oldIdx, newIdx);

          // Apply this order to ALL flights
          const numFlights = session.numFlights || 1;
          const newSquads = [];
          for (let f = 1; f <= numFlights; f++) {
            for (const squadNum of newSquadOrder) {
              newSquads.push(`${f}-${squadNum}`);
            }
          }
          setLocalSquads(newSquads);

          // Save to rotation
          let squadOrder = newSquadOrder.map(s => parseInt(s));

          // Reverse the rotation offset to get base order
          let newBaseOrder = [...squadOrder];
          if (selectedRotation > 1) {
            const total = squadOrder.length;
            const rotationOffset = selectedRotation - 1;
            const part1 = squadOrder.slice(rotationOffset);
            const part2 = squadOrder.slice(0, rotationOffset);
            newBaseOrder = [...part1, ...part2];
          }

          if (rotation) {
            const previousSquadAssignments = [...(rotation.squadAssignments || [])];
            const previousLocalSquads = [...localSquads];

            const newOrderString = newBaseOrder.join('');
            const newSquadAssignments = [...(rotation.squadAssignments || [])];
            newSquadAssignments[0] = newOrderString;

            const updatedRotation = {
              ...rotation,
              squadAssignments: newSquadAssignments
            };

            const rotationIndex = currentCompetition.rotations.findIndex(r => r.id === rotation.id);
            if (rotationIndex !== -1) {
              const newRotations = [...currentCompetition.rotations];
              newRotations[rotationIndex] = updatedRotation;

              const undoSquadReorder = () => {
                setLocalSquads(previousLocalSquads);
                const restoredRotation = {
                  ...rotation,
                  squadAssignments: previousSquadAssignments
                };
                const restoredRotations = [...currentCompetition.rotations];
                restoredRotations[rotationIndex] = restoredRotation;
                dispatch(updateCompetition({
                  ...currentCompetition,
                  rotations: restoredRotations
                }));
                dispatch(setToast({ message: 'Squad reorder undone', severity: 'info' }));
              };

              dispatch(updateCompetition({
                ...currentCompetition,
                rotations: newRotations
              }));
              dispatch(setToast({
                message: 'Squad order updated',
                severity: 'success',
                undoAction: undoSquadReorder
              }));
            }
          }
        }
      } else {
        // Single flight mode
        const oldIndex = localSquads.indexOf(active.id);
        const newIndex = localSquads.indexOf(over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newSquads = arrayMove(localSquads, oldIndex, newIndex);
          setLocalSquads(newSquads);

          const squadOrder = newSquads.map(s => typeof s === 'string' ? parseInt(s) : s);

          // Reverse the rotation offset to get base order
          let newBaseOrder = [...squadOrder];
          if (selectedRotation > 1) {
            const rotationOffset = selectedRotation - 1;
            const part1 = squadOrder.slice(rotationOffset);
            const part2 = squadOrder.slice(0, rotationOffset);
            newBaseOrder = [...part1, ...part2];
          }

          if (rotation) {
            const previousSquadAssignments = [...(rotation.squadAssignments || [])];
            const previousLocalSquads = [...localSquads];

            const newOrderString = newBaseOrder.join('');
            const newSquadAssignments = [...(rotation.squadAssignments || [])];
            newSquadAssignments[0] = newOrderString;

            const updatedRotation = {
              ...rotation,
              squadAssignments: newSquadAssignments
            };

            const rotationIndex = currentCompetition.rotations.findIndex(r => r.id === rotation.id);
            if (rotationIndex !== -1) {
              const newRotations = [...currentCompetition.rotations];
              newRotations[rotationIndex] = updatedRotation;

              const undoSquadReorder = () => {
                setLocalSquads(previousLocalSquads);
                const restoredRotation = {
                  ...rotation,
                  squadAssignments: previousSquadAssignments
                };
                const restoredRotations = [...currentCompetition.rotations];
                restoredRotations[rotationIndex] = restoredRotation;
                dispatch(updateCompetition({
                  ...currentCompetition,
                  rotations: restoredRotations
                }));
                dispatch(setToast({ message: 'Squad reorder undone', severity: 'info' }));
              };

              dispatch(updateCompetition({
                ...currentCompetition,
                rotations: newRotations
              }));
              dispatch(setToast({
                message: 'Squad order updated',
                severity: 'success',
                undoAction: undoSquadReorder
              }));
            }
          }
        }
      }
    }
  };

  const handleAthleteDragEnd = (event) => {
    const { active, over } = event;
    const savedDropIndicator = dropIndicator;

    setActiveAthleteId(null);
    setDropIndicator(null);

    // Don't process if there's no valid drop location
    if (!over || !savedDropIndicator || !active) return;

    const { squad: destSquadKey, index: insertIndex } = savedDropIndicator;

    // Store current state for undo functionality
    const previousState = JSON.parse(JSON.stringify(squadGymnastsList));
    const previousGymnasts = JSON.parse(JSON.stringify(gymnasts));

    // Parse destSquadKey to extract flight and squad numbers
    let destFlightNumber, destSquadNumber;
    if (allFlights && typeof destSquadKey === 'string' && destSquadKey.includes('-')) {
      // Format is "flight-squad" (e.g., "1-1", "2-3")
      const parts = destSquadKey.split('-');
      destFlightNumber = parseInt(parts[0]);
      destSquadNumber = parseInt(parts[1]);
    } else {
      // Single flight mode - use the current flight number
      destFlightNumber = flightNumber;
      destSquadNumber = typeof destSquadKey === 'string' ? parseInt(destSquadKey) : destSquadKey;
    }

    // Determine what is being moved
    const isMultiDrag = selectedAthleteIds.includes(active.id);
    const movingIds = isMultiDrag ? selectedAthleteIds : [active.id];

    // Get all source gymnasts involved
    const allGymnasts = Object.values(squadGymnastsList).flat();
    const movingGymnasts = [];

    // Preserve the order of selectedAthleteIds if multi-drag
    if (isMultiDrag) {
       selectedAthleteIds.forEach(id => {
         const g = allGymnasts.find(gym => gym.id === id);
         if (g) movingGymnasts.push(g);
       });
    } else {
       const g = allGymnasts.find(gym => gym.id === active.id);
       if (g) movingGymnasts.push(g);
    }

    // Perform the move: remove from source squads and insert into destination
    const newSquadGymnastsList = JSON.parse(JSON.stringify(squadGymnastsList));

    // 1. Remove moving items from all squads
    Object.keys(newSquadGymnastsList).forEach(squadKey => {
      newSquadGymnastsList[squadKey] = newSquadGymnastsList[squadKey].filter(g => !movingIds.includes(g.id));
    });

    // 2. Update squad and flight assignment for moving gymnasts
    const updatedMovingGymnasts = movingGymnasts.map(g => ({
      ...g,
      squad: String.fromCharCode(64 + destSquadNumber),
      flight: String.fromCharCode(64 + destFlightNumber)
    }));

    // 3. Insert into destination squad at the calculated index
    // Adjust insertIndex if we removed items from the same squad before the insertion point
    let adjustedInsertIndex = insertIndex;
    const sourceSquadKey = Object.keys(squadGymnastsList).find(key =>
      squadGymnastsList[key].some(g => movingIds.includes(g.id))
    );

    // Check if position actually changed (for single item moves within same squad)
    if (sourceSquadKey === destSquadKey && movingIds.length === 1) {
      const originalList = squadGymnastsList[destSquadKey];
      const originalIndex = originalList.findIndex(g => g.id === movingIds[0]);

      // Count how many items were removed before the insertion point
      let removedBeforeInsert = 0;
      for (let i = 0; i < insertIndex && i < originalList.length; i++) {
        if (movingIds.includes(originalList[i].id)) {
          removedBeforeInsert++;
        }
      }
      adjustedInsertIndex = insertIndex - removedBeforeInsert;

      // If the position didn't change, skip the update
      if (originalIndex === adjustedInsertIndex) {
        return;
      }
    } else if (sourceSquadKey === destSquadKey) {
      // Multi-item move within same squad
      const originalList = squadGymnastsList[destSquadKey];
      let removedBeforeInsert = 0;
      for (let i = 0; i < insertIndex && i < originalList.length; i++) {
        if (movingIds.includes(originalList[i].id)) {
          removedBeforeInsert++;
        }
      }
      adjustedInsertIndex = insertIndex - removedBeforeInsert;
    }

    newSquadGymnastsList[destSquadKey].splice(adjustedInsertIndex, 0, ...updatedMovingGymnasts);

    // 4. Recalculate orders for ALL squads
    const finalGymnastsToUpdate = [];
    Object.keys(newSquadGymnastsList).forEach(squadKey => {
       const list = newSquadGymnastsList[squadKey];
       const updatedList = list.map((g, idx) => {
         const newOrder = idx + 1;
         const newApparatus = session.gender?.toLowerCase() === 'm' && idx < MENS_APPARATUS.length ?
            MENS_APPARATUS[idx]?.id : null;

         return {
           ...g,
           order: newOrder,
           apparatus: newApparatus
         };
       });

       newSquadGymnastsList[squadKey] = updatedList;
       finalGymnastsToUpdate.push(...updatedList);
    });

    // Optimistically update local state
    setSquadGymnastsList(newSquadGymnastsList);

    // 5. Dispatch update - filter out all gymnasts from this session that we're managing
    const otherGymnasts = allFlights
      ? gymnasts.filter(g => Number(g.session) !== Number(session.sessionNumber))
      : gymnasts.filter(g =>
          !(Number(g.session) === Number(session.sessionNumber) &&
            g.flight === String.fromCharCode(64 + flightNumber) &&
            squads.includes(parseInt(g.squad.charCodeAt(0) - 64)))
        );

    const allUpdated = [...otherGymnasts, ...finalGymnastsToUpdate];
    
    // Create undo function
    const undoMove = () => {
      // Restore previous squad assignments
      setSquadGymnastsList(previousState);
      
      // Restore previous gymnasts data
      onUpdateOrders?.(previousGymnasts);
      
      // Show undo confirmation toast
      dispatch(updateCompetition({
        type: 'gymnasts',
        data: previousGymnasts,
        showToast: true,
        toastMessage: 'Move undone successfully'
      }));
    };

    // Dispatch update with undo toast
    onUpdateOrders?.(allUpdated);
    
    // Show success toast with undo option
    const athleteCount = movingGymnasts.length;
    const athleteText = athleteCount === 1 ? 'athlete' : 'athletes';
    dispatch(updateCompetition({
      type: 'gymnasts',
      data: allUpdated,
      showToast: true,
      toastMessage: `Moved ${athleteCount} ${athleteText} successfully`,
      undoAction: undoMove
    }));

    // Clear selection after move
    setSelectedAthleteIds([]);
  };

  const renderSquadCard = (squadKey, isDragging = false, index = -1) => {
    // Show skeleton if squad not yet loaded (progressive loading)
    // Skip skeleton if there are no gymnasts to avoid flicker on empty state
    const isSquadLoaded = loadedSquads.has(squadKey);
    if (!isSquadLoaded && isLoadingSquads && sessionGymnasts.length > 0) {
      return (
        <Box
          ref={el => squadRefs.current[squadKey] = el}
          data-squad-key={squadKey}
          sx={{ display: 'flex', width: '100%' }}
        >
          <SquadCardSkeleton competitionColors={competitionColors} />
        </Box>
      );
    }

    const squadGymnasts = squadGymnastsList[squadKey] || [];

    // Parse squadKey to extract flight and squad numbers
    let flightNum, squadNum;
    if (allFlights && typeof squadKey === 'string' && squadKey.includes('-')) {
      const parts = squadKey.split('-');
      flightNum = parseInt(parts[0]);
      squadNum = parseInt(parts[1]);
    } else {
      flightNum = flightNumber;
      squadNum = typeof squadKey === 'string' ? parseInt(squadKey) : squadKey;
    }

    let displayLetter = String.fromCharCode(64 + squadNum);
    let flightLetter = String.fromCharCode(64 + flightNum);
    let gridIndex = squadNum - 1;

    // Calculate gridIndex based on visual position for apparatus assignment
    // displayLetter stays tied to the actual squad number so it moves with the squad
    if (index >= 0) {
        if (allFlights) {
          const squadsPerFlight = squads.length;
          const squadIndex = index % squadsPerFlight;
          gridIndex = squadIndex;
        } else {
          gridIndex = index;
        }
    } else if (isDragging && localSquads.length > 0) {
        const currentIndex = localSquads.indexOf(squadKey);
        if (currentIndex !== -1) {
          if (allFlights) {
            const squadsPerFlight = squads.length;
            const squadIndex = currentIndex % squadsPerFlight;
            gridIndex = squadIndex;
          } else {
            gridIndex = currentIndex;
          }
        }
    }

    // Check if this squad has a drop indicator
    const hasDropIndicator = dropIndicator && dropIndicator.squad === squadKey;

    const cardContent = (
      <Card
        sx={{
          p: 1,
          minHeight: 140,
          height: 'auto',
          width: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          border: moveSquadsMode && isDragging
            ? `2px dashed ${competitionColors?.secondary || '#f50057'}80`
            : `1px solid ${competitionColors?.primary || '#2196f3'}20`,
          backgroundColor: moveSquadsMode && isDragging
            ? `${competitionColors?.secondary || '#f50057'}66`
            : moveSquadsMode
               ? `${competitionColors?.secondary || '#f50057'}08`
               : 'background.paper',
          '&:hover': {
            borderColor: competitionColors?.primary || '#2196f3',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          },
          position: 'relative',
          zIndex: isDragging ? 1000 : 1,
          boxShadow: isDragging ? '0 8px 16px rgba(0,0,0,0.2)' : 'none',
          cursor: moveSquadsMode ? 'grab' : 'default',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={0.5}
          sx={{
            '& .MuiButton-root': {
              minWidth: 'auto',
              whiteSpace: 'nowrap'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {moveSquadsMode && <DragIndicatorIcon sx={{ color: 'white', mr: 0.5 }} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 500, my: 0 }}>
              Squad {displayLetter}
            </Typography>
            <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: '0.9em' }}>
              ({squadGymnasts.length})
            </Typography>
          </Box>
          {/* Apparatus abbreviation - based on grid position, not squad number */}
          {(() => {
            const isMens = session.gender?.toLowerCase() === 'm';
            const apparatus = isMens ? MENS_APPARATUS : WOMENS_APPARATUS;
            const numApparatus = apparatus.length;
            // Use gridIndex (visual position) so apparatus stays fixed when squads rotate
            const apparatusIndex = gridIndex % numApparatus;
            const normalizedIndex = apparatusIndex < 0 ? apparatusIndex + numApparatus : apparatusIndex;
            return (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: '#ffffff',
                  backgroundColor: theme => theme.palette.mode === 'dark' ? '#555555' : '#757575',
                  minWidth: '24px',
                  height: '24px',
                  px: 0.5,
                  borderRadius: 0.5,
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'monospace'
                }}
              >
                {apparatus[normalizedIndex]?.abbrev}
              </Typography>
            );
          })()}
        </Stack>

        {/* Athlete List */}
        <SortableContext
           id={squadKey}
           items={squadGymnasts.map(g => g.id)}
           strategy={verticalListSortingStrategy}
        >
          <List
            dense
            sx={{
              opacity: moveSquadsMode ? 1 : 1,
              filter: moveSquadsMode ? 'blur(3px)' : 'none',
              pointerEvents: moveSquadsMode ? 'none' : 'auto',
              transition: 'all 0.2s ease',
              minHeight: viewByTeams ? 'auto' : 80,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {viewByTeams ? (
              // Group athletes by team and render compact team rows
              (() => {
                const teamGroups = squadGymnasts.reduce((acc, g) => {
                  const teamId = g.teamId != null ? g.teamId : 'unattached';
                  if (!acc[teamId]) acc[teamId] = [];
                  acc[teamId].push(g);
                  return acc;
                }, {});
                const sortedTeamIds = Object.keys(teamGroups).sort((a, b) => {
                  if (a === 'unattached') return 1;
                  if (b === 'unattached') return -1;
                  const teamA = teamMap[a] || teamMap[Number(a)];
                  const teamB = teamMap[b] || teamMap[Number(b)];
                  return (teamA?.name || teamA?.shortName || '').localeCompare(teamB?.name || teamB?.shortName || '');
                });
                return sortedTeamIds.map((teamId, idx) => {
                  const teamAthletes = teamGroups[teamId];
                  const team = teamId !== 'unattached' ? (teamMap[teamId] || teamMap[Number(teamId)]) : null;
                  const teamName = decodeHtmlEntities(team?.name || team?.shortName) || 'Unattached';
                  const teamColor = highlightTeamsEnabled && teamColors?.[teamId] ? teamColors[teamId] : null;
                  return (
                    <React.Fragment key={teamId}>
                      <ListItem
                        sx={{
                          py: 0.25,
                          px: 1,
                          minHeight: 28,
                          backgroundColor: teamColor ? `${teamColor}15` : 'transparent',
                          borderLeft: teamColor ? `3px solid ${teamColor}` : 'none',
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                {teamName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {teamAthletes.length}
                              </Typography>
                            </Box>
                          }
                          sx={{ my: 0 }}
                        />
                      </ListItem>
                      {idx < sortedTeamIds.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  );
                });
              })()
            ) : squadGymnasts.map((gymnast, index) => {
              const isSelected = selectedAthleteIds.includes(gymnast.id);
              const selectionIndex = selectedAthleteIds.indexOf(gymnast.id) + 1;
              const scratchSelected = scratchMode ? scratchSelectionSet.has(gymnast.id) : gymnast.scratched;

              // Check neighbors to style adjacent selections as a group
              const prevSelected = index > 0 && selectedAthleteIds.includes(squadGymnasts[index - 1].id);
              const nextSelected = index < squadGymnasts.length - 1 && selectedAthleteIds.includes(squadGymnasts[index + 1].id);

              const renderItem = (clickHandler) => (
                <AthleteListItem
                  gymnast={gymnast}
                  index={index}
                  gridIndex={gridIndex}
                  squadNum={squadNum}
                  selectedRotation={selectedRotation}
                  isSelected={isSelected}
                  selectionIndex={selectionIndex}
                  isFirstInGroup={isSelected && !prevSelected}
                  isLastInGroup={isSelected && !nextSelected}
                  moveAthletesMode={moveAthletesMode}
                  competitionColors={competitionColors}
                  teamColors={teamColors}
                  levelColors={levelColors}
                  highlightTeamsEnabled={highlightTeamsEnabled}
                  highlightLevelsEnabled={highlightLevelsEnabled}
                  highlightedTeamId={highlightedTeamId}
                  teamMap={teamMap}
                  session={session}
                  scratchMode={scratchMode}
                  isScratchSelected={scratchSelected}
                  isScratched={gymnast.scratched}
                  onClick={clickHandler}
                />
              );

              // Render drop indicator before this item if needed
              const dropIndicatorElement = hasDropIndicator && dropIndicator.index === index ? (
                <Box
                  key={`drop-indicator-${squadKey}-${index}`}
                  sx={{
                    height: 4,
                    backgroundColor: '#2196f3',
                    borderRadius: 2,
                    mx: 2,
                    my: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.2s ease'
                  }}
                />
              ) : null;

              if (moveAthletesMode) {
                const isGlobalDragging = activeAthleteId !== null;
                const isPartofGroupDrag = isGlobalDragging && selectedAthleteIds.includes(gymnast.id);

                return (
                  <React.Fragment key={gymnast.id || index}>
                    {dropIndicatorElement}
                    <SortableAthleteItem
                      id={gymnast.id}
                      selected={isSelected}
                      selectionIndex={selectionIndex}
                      isFirstInGroup={isSelected && !prevSelected}
                      onClick={(e) => handleAthleteClick(e, gymnast.id)}
                      forceDragging={isPartofGroupDrag}
                    >
                      {renderItem()}
                    </SortableAthleteItem>
                    {index < squadGymnasts.length - 1 && !(isSelected && selectedAthleteIds.includes(squadGymnasts[index + 1].id)) && (
                      <Divider component="li" sx={{ pointerEvents: 'none' }} />
                    )}
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={gymnast.id || index}>
                   {dropIndicatorElement}
                   {renderItem(scratchMode ? (e) => handleAthleteClick(e, gymnast.id) : undefined)}
                   {index < squadGymnasts.length - 1 && !(isSelected && selectedAthleteIds.includes(squadGymnasts[index + 1].id)) && <Divider component="li" />}
                </React.Fragment>
              );
            })}
            {/* Drop indicator at the end if needed */}
            {hasDropIndicator && dropIndicator.index === squadGymnasts.length && (
              <Box
                key={`drop-indicator-end-${squadKey}`}
                sx={{
                  height: 4,
                  backgroundColor: '#2196f3',
                  borderRadius: 2,
                  mx: 2,
                  my: 1,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  transition: 'all 0.2s ease'
                }}
              />
            )}
            {squadGymnasts.length === 0 && (
               <ListItem
                 sx={{
                   display: 'flex',
                   alignItems: 'center',
                   justifyContent: 'center',
                   flexGrow: 1,
                   minHeight: 60,
                   py: 2
                 }}
               >
                 <ListItemText
                   primary="No athletes"
                   sx={{
                     color: 'text.secondary',
                     fontStyle: 'italic',
                     fontSize: '0.875rem',
                     textAlign: 'center'
                   }}
                 />
               </ListItem>
            )}
          </List>
        </SortableContext>
      </Card>
    );

    if (moveAthletesMode) {
      return (
        <DroppableSquad id={squadKey}>
          {cardContent}
        </DroppableSquad>
      );
    }

    return cardContent;
  };

  if (moveSquadsMode) {
    // Group squads by flight for consistent display
    const flightGroups = {};
    if (allFlights) {
      localSquads.forEach(squadKey => {
        let fNum = flightNumber;
        if (typeof squadKey === 'string' && squadKey.includes('-')) {
          fNum = parseInt(squadKey.split('-')[0]);
        }
        if (!flightGroups[fNum]) flightGroups[fNum] = [];
        flightGroups[fNum].push(squadKey);
      });
    } else {
      flightGroups[flightNumber] = localSquads;
    }

    // Custom collision detection that only allows drops within the same flight
    const sameFlightCollision = (args) => {
      const collisions = closestCenter(args);
      if (!allFlights || !dragSourceFlight) return collisions;

      // Filter out collisions with items from different flights
      return collisions.filter(collision => {
        const targetKey = String(collision.id);
        const targetFlight = targetKey.split('-')[0];
        return targetFlight === dragSourceFlight;
      });
    };

    return (
      <Box sx={{ mt: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={sameFlightCollision}
          onDragStart={handleDragStart}
          onDragOver={handleSquadDragOver}
          onDragEnd={handleSquadDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={localSquads}
            strategy={rectSortingStrategy}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {Object.entries(flightGroups).map(([fNumStr, squadKeys], groupIndex) => {
                const fNum = parseInt(fNumStr);
                const flightLetter = String.fromCharCode(64 + fNum);
                const flightAthleteCount = sessionGymnasts.filter(g => g.flight === flightLetter && (scratchMode || !g.scratched)).length;

                return (
                  <Box
                    key={fNum}
                    id={`flight-${flightLetter}`}
                    sx={{
                      scrollMarginTop: '120px',
                      px: 2,
                      pt: groupIndex === 0 ? 2 : 1.5,
                      pb: 2
                    }}
                  >
                    {/* Flight header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ mb: 0 }}>
                          Flight {flightLetter} ({flightAthleteCount})
                        </Typography>
                        <Chip
                          label={`Rotation ${fNum === 1 ? selectedRotation : (selectedRotationB || selectedRotation)}`}
                          size="small"
                          sx={{
                            backgroundColor: competitionColors?.primary || '#2196f3',
                            color: '#ffffff',
                            height: '24px',
                            mt: '2px'
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${rotation?.numSquads <= 4 ? 4 : 3}, minmax(0, 1fr))`,
                        gap: 2,
                        width: '100%',
                        alignItems: 'flex-start',
                        justifyItems: 'stretch'
                      }}
                    >
                      {squadKeys.map((squadKey) => (
                        <SortableSquadItem key={squadKey} id={squadKey}>
                          {(listeners, isDragging) => (
                            <Box sx={{ display: 'flex', width: '100%' }}>
                              <Box {...listeners} style={{ height: '100%', width: '100%' }}>
                                {renderSquadCard(squadKey, false, localSquads.indexOf(squadKey))}
                              </Box>
                            </Box>
                          )}
                        </SortableSquadItem>
                      ))}
                    </Box>
                    {/* Add divider between flights */}
                    {groupIndex < Object.keys(flightGroups).length - 1 && (
                      <Divider sx={{ my: 2, borderBottomWidth: 2, borderColor: 'divider' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </SortableContext>
          <DragOverlay dropAnimation={dropAnimation}>
            {activeId ? renderSquadCard(activeId, true) : null}
          </DragOverlay>
        </DndContext>
      </Box>
    );
  }

  if (moveAthletesMode) {
    // Group squads by flight
    const flightGroups = {};
    if (allFlights) {
      localSquads.forEach(squadKey => {
        let fNum = flightNumber;
        if (typeof squadKey === 'string' && squadKey.includes('-')) {
          fNum = parseInt(squadKey.split('-')[0]);
        }
        if (!flightGroups[fNum]) flightGroups[fNum] = [];
        flightGroups[fNum].push(squadKey);
      });
    } else {
      flightGroups[flightNumber] = localSquads;
    }

    return (
      <Box sx={{ mt: 1 }}>
        <DndContext
          sensors={sensors}
          collisionDetection={(args) => {
            // First find which squad container the pointer is within
            const pointerCollisions = pointerWithin(args);
            const squadContainers = pointerCollisions.filter(c => localSquadsSet.has(c.id)); // O(1) Set lookup

            if (squadContainers.length > 0) {
              const squadId = squadContainers[0].id;
              const athleteList = squadGymnastsList[squadId] || [];

              if (athleteList.length === 0) {
                // Empty squad - return the squad container
                return [{ id: squadId }];
              }

              // Create Set for O(1) athlete lookups
              const athleteIdSet = new Set(athleteList.map(g => g.id));

              // Get pointer Y coordinate
              const pointerY = args.pointerCoordinates?.y;

              // Find the rect of the last athlete in this squad
              const lastAthleteId = athleteList[athleteList.length - 1].id;
              const lastAthleteRect = args.droppableRects.get(lastAthleteId);

              // If pointer is below the last athlete's bottom, return squad container for end-of-list
              if (pointerY && lastAthleteRect && pointerY > lastAthleteRect.bottom) {
                return [{ id: squadId }];
              }

              // For positions within the list, find the athlete we're closest to
              // Use rectIntersection for more precise targeting
              const allCollisions = rectIntersection(args);
              const athleteCollisions = allCollisions.filter(c => athleteIdSet.has(c.id)); // O(1) Set lookup

              if (athleteCollisions.length > 0) {
                return athleteCollisions;
              }

              // Fallback to closestCenter if no intersection found
              const centerCollisions = closestCenter(args);
              const centerAthleteCollisions = centerCollisions.filter(c => athleteIdSet.has(c.id)); // O(1) Set lookup

              if (centerAthleteCollisions.length > 0) {
                return centerAthleteCollisions;
              }

              // Return squad container as last resort
              return [{ id: squadId }];
            }

            // Fallback to closestCenter
            return closestCenter(args);
          }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleAthleteDragEnd}
          onDragCancel={handleDragCancel}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {Object.entries(flightGroups).map(([fNumStr, squadKeys], groupIndex) => {
                const fNum = parseInt(fNumStr);
                const flightLetter = String.fromCharCode(64 + fNum);

                // Calculate athlete count for this flight
                const flightAthleteCount = sessionGymnasts.filter(g => g.flight === flightLetter && (scratchMode || !g.scratched)).length;

                return (
                  <Box
                    key={fNum}
                    id={`flight-${flightLetter}`}
                    sx={{
                      scrollMarginTop: '120px',
                      px: 2,
                      pt: groupIndex === 0 ? 2 : 1.5,
                      pb: 2
                    }}
                  >
                    {/* Flight header with collapse/expand button */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ mb: 0 }}>
                          Flight {flightLetter} ({flightAthleteCount})
                        </Typography>
                        <Chip
                          label={`Rotation ${fNum === 1 ? selectedRotation : (selectedRotationB || selectedRotation)}`}
                          size="small"
                          sx={{
                            backgroundColor: competitionColors?.primary || '#2196f3',
                            color: '#ffffff',
                            height: '24px',
                            mt: '2px'
                          }}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => toggleFlightCollapse(fNum)}
                        sx={{
                          color: competitionColors?.primary || '#2196f3',
                          '&:hover': {
                            backgroundColor: `${competitionColors?.primary || '#2196f3'}11`
                          }
                        }}
                      >
                        {collapsedFlights[fNum] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                      </IconButton>
                    </Box>
                    <Collapse in={!collapsedFlights[fNum]} timeout={300}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${rotation?.numSquads <= 4 ? 4 : 3}, minmax(0, 1fr))`,
                          gap: 2,
                          width: '100%',
                          alignItems: 'flex-start',
                          justifyItems: 'stretch',
                          mt: 2
                        }}
                      >
                        {squadKeys.map((squadKey) => (
                          <Box key={squadKey} sx={{ display: 'flex', width: '100%' }}>
                            {/* Pass the GLOBAL index from localSquads to ensure correct lettering (A, B, C...) */}
                            {renderSquadCard(squadKey, false, localSquads.indexOf(squadKey))}
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                    {/* Add divider between flights */}
                    {groupIndex < Object.keys(flightGroups).length - 1 && (
                      <Divider sx={{ my: 2, borderBottomWidth: 2, borderColor: 'divider' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
            <DragOverlay dropAnimation={null}>
              {activeAthleteId ? (
                 <MultiDragPreview
                    activeId={activeAthleteId}
                    selectedIds={selectedAthleteIds.includes(activeAthleteId) ? selectedAthleteIds : [activeAthleteId]}
                    squadGymnastsList={squadGymnastsList}
                    squads={localSquads}
                    competitionColors={competitionColors}
                    teamColors={teamColors}
                    levelColors={levelColors}
                    highlightTeamsEnabled={highlightTeamsEnabled}
                    highlightLevelsEnabled={highlightLevelsEnabled}
                    highlightedTeamId={highlightedTeamId}
                    session={session}
                    teamMap={teamMap}
                 />
              ) : null}
            </DragOverlay>
        </DndContext>
      </Box>
     );
  }

  // Default View
  if (allFlights) {
    // Group squads by flight for consistent rendering
    const flightGroups = {};
    localSquads.forEach(squadKey => {
      let fNum = flightNumber;
      if (typeof squadKey === 'string' && squadKey.includes('-')) {
        fNum = parseInt(squadKey.split('-')[0]);
      }
      if (!flightGroups[fNum]) flightGroups[fNum] = [];
      flightGroups[fNum].push(squadKey);
    });

    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {Object.entries(flightGroups).map(([fNumStr, squadKeys], groupIndex) => {
            const fNum = parseInt(fNumStr);
            const flightLetter = String.fromCharCode(64 + fNum);
            const flightAthleteCount = sessionGymnasts.filter(g => g.flight === flightLetter && (scratchMode || !g.scratched)).length;

            return (
              <Box
                key={fNum}
                id={`flight-${flightLetter}`}
                sx={{
                  scrollMarginTop: '120px',
                  px: 2,
                  pt: groupIndex === 0 ? 2 : 1.5,
                  pb: 2
                }}
              >
                {/* Flight header with collapse/expand button */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" sx={{ mb: 0 }}>
                      Flight {flightLetter} ({flightAthleteCount})
                    </Typography>
                    <Chip
                      label={`Rotation ${fNum === 1 ? selectedRotation : (selectedRotationB || selectedRotation)}`}
                      size="small"
                      sx={{
                        backgroundColor: competitionColors?.primary || '#2196f3',
                        color: '#ffffff',
                        height: '24px',
                        mt: '2px'
                      }}
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => toggleFlightCollapse(fNum)}
                    sx={{
                      color: competitionColors?.primary || '#2196f3',
                      '&:hover': {
                        backgroundColor: `${competitionColors?.primary || '#2196f3'}11`
                      }
                    }}
                  >
                    {collapsedFlights[fNum] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
                  </IconButton>
                </Box>
                <Collapse in={!collapsedFlights[fNum]} timeout={300}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${rotation?.numSquads <= 4 ? 4 : 3}, minmax(0, 1fr))`,
                      gap: 2,
                      width: '100%',
                      alignItems: 'flex-start',
                      justifyItems: 'stretch',
                      mt: 2
                    }}
                  >
                    {squadKeys.map((squadKey) => (
                      <Box key={squadKey} sx={{ display: 'flex', width: '100%' }}>
                        {renderSquadCard(squadKey, false, localSquads.indexOf(squadKey))}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
                {/* Add divider between flights */}
                {groupIndex < Object.keys(flightGroups).length - 1 && (
                  <Divider sx={{ my: 2, borderBottomWidth: 2, borderColor: 'divider' }} />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Single flight view
  const flightLetter = String.fromCharCode(64 + flightNumber);
  const flightAthleteCount = sessionGymnasts.filter(g => g.flight === flightLetter && (scratchMode || !g.scratched)).length;

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        id={`flight-${flightLetter}`}
        sx={{
          scrollMarginTop: '120px',
          px: 2,
          pt: 2,
          pb: 2
        }}
      >
        {/* Flight header with collapse/expand button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ mb: 0 }}>
              Flight {flightLetter} ({flightAthleteCount})
            </Typography>
            <Chip
              label={`Rotation ${selectedRotation}`}
              size="small"
              sx={{
                backgroundColor: competitionColors?.primary || '#2196f3',
                color: '#ffffff',
                height: '24px',
                mt: '2px'
              }}
            />
          </Box>
          <IconButton
            size="small"
            onClick={() => toggleFlightCollapse(flightNumber)}
            sx={{
              color: competitionColors?.primary || '#2196f3',
              '&:hover': {
                backgroundColor: `${competitionColors?.primary || '#2196f3'}11`
              }
            }}
          >
            {collapsedFlights[flightNumber] ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        </Box>
        <Collapse in={!collapsedFlights[flightNumber]} timeout={300}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(${rotation?.numSquads <= 4 ? 4 : 3}, minmax(0, 1fr))`,
              gap: 2,
              width: '100%',
              alignItems: 'flex-start',
              mt: 2
            }}
          >
            {localSquads.map((squadKey, gridIndex) => (
                <Box key={squadKey} sx={{ display: 'flex', width: '100%' }}>
                    {renderSquadCard(squadKey, false, gridIndex)}
                </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default React.memo(SessionSquad);
